import { useCallback, useMemo, useState } from 'react';

import { Job, JobEntity, JobOperation, useLoggedInUser } from '@commenda/api';
import { useToggle } from '@commenda/kakashi';
import {
  IndirectTaxFilingV2ExportDeliveryType,
  IndirectTaxFilingV2ExportMode,
  IIndirectTaxFilingV2MutationResult,
  exportIndirectTaxFilingV2LiabilityReport,
  exportIndirectTaxFilingV2TaxBreakdowns,
  exportIndirectTaxFilingV2TaxBreakdownsWithLineItems,
  triggerIndirectTaxFilingsV2BreakdownGeneration,
  triggerIndirectTaxFilingsV2ContributionSync,
} from '@commenda/api/indirect-tax/filings';

import { IndirectTaxRegion, useIndirectTaxJobWatcher } from '../../common';
import { useNotification } from '../../../hooks/useNotification';
import {
  IndirectTaxFilingActionKey,
  IndirectTaxFilingActionsHelper,
} from '../helpers';

/** A single ready-to-render menu entry: label + enabled state + handler. */
export interface IndirectTaxFilingMenuAction {
  key: IndirectTaxFilingActionKey;
  label: string;
  disabled: boolean;
  onClick: () => void;
}

interface UseIndirectTaxFilingActionsMenuParams {
  filingId: string;
  region: IndirectTaxRegion;
  corporationId: number;
  jurisdictionId: string;
  userEmail?: string;
  showInternalActions?: boolean;
  onActionComplete?: () => void;
}

interface UseIndirectTaxFilingActionsMenuResult {
  isInternal: boolean;
  isLoading: boolean;
  error: unknown;
  actions: IndirectTaxFilingMenuAction[];
  jobsLogDialogVisibility: {
    visible: boolean;
    show: () => void;
    hide: () => void;
  };
  exportJobs: Job[];
}

/** A mutation a menu action fires; one per action that hits the backend. */
type FilingActionRunner = () => Promise<
  IIndirectTaxFilingV2MutationResult<unknown>
>;

/**
 * Drives the indirect-tax filing detail kebab menu: resolves the internal
 * gate, fetches this filing's export jobs for the "view generated reports"
 * dialog, and dispatches the region-appropriate generate/export mutations
 * (all v2 endpoints). Returns data only — the component renders it.
 */
export function useIndirectTaxFilingActionsMenu({
  filingId,
  region,
  corporationId,
  jurisdictionId,
  userEmail,
  showInternalActions,
  onActionComplete,
}: UseIndirectTaxFilingActionsMenuParams): UseIndirectTaxFilingActionsMenuResult {
  const { data: loggedInUser, error: userError } = useLoggedInUser();
  const { setNotification } = useNotification();
  const jobsLogDialogVisibility = useToggle();
  const [isBusy, setIsBusy] = useState(false);

  const isInternal =
    showInternalActions ??
    IndirectTaxFilingActionsHelper.isInternalUser(loggedInUser?.email);

  const { getJobsByEntityAndOperation } = useIndirectTaxJobWatcher({
    corporation_id: corporationId,
  });

  const exportJobs = useMemo(
    () =>
      [
        ...getJobsByEntityAndOperation(JobEntity.FILING, JobOperation.EXPORT),
        ...getJobsByEntityAndOperation(
          JobEntity.FILING,
          JobOperation.EXPORT_JURISDICTION_TAX_REPORT
        ),
      ]
        .filter((job) => job.entity_id === filingId)
        .sort((a, b) => b.submitted_at - a.submitted_at),
    [getJobsByEntityAndOperation, filingId]
  );

  const regionActionKeys = useMemo(
    () => IndirectTaxFilingActionsHelper.getActionKeysForRegion(region),
    [region]
  );

  const isJurisdictionReportSupported =
    IndirectTaxFilingActionsHelper.isJurisdictionTaxReportSupported(
      jurisdictionId
    );

  const notifyResult = useCallback(
    (
      result: IIndirectTaxFilingV2MutationResult<unknown>,
      successMessage: string,
      onSuccess?: () => void
    ) => {
      if (result.success) {
        setNotification({
          show: true,
          valid: true,
          title: 'Success',
          message: successMessage,
        });
        onSuccess?.();
      } else {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: result.error || 'Something went wrong',
        });
      }
    },
    [setNotification]
  );

  // Side-effectful wiring stays in the hook; the pure action metadata
  // (labels, region split, email/refresh rules, success copy) lives on the
  // helper. Each runner builds its own payload because the v2 endpoints
  // take different shapes (singular filingId vs filingIds[], delivery, etc.).
  const mutationRunners = useMemo<
    Partial<Record<IndirectTaxFilingActionKey, FilingActionRunner>>
  >(() => {
    const filingIds = [filingId];
    const exportParams = {
      corporationId,
      filingId,
      deliveryType: IndirectTaxFilingV2ExportDeliveryType.EMAIL,
      deliveryRecipients: userEmail ? [userEmail] : [],
    };
    return {
      [IndirectTaxFilingActionKey.GENERATE_BREAKDOWNS]: () =>
        triggerIndirectTaxFilingsV2BreakdownGeneration({
          params: { corporationId, filingIds },
        }),
      [IndirectTaxFilingActionKey.EXPORT_BREAKDOWN]: () =>
        exportIndirectTaxFilingV2TaxBreakdowns({ params: exportParams }),
      [IndirectTaxFilingActionKey.EXPORT_BREAKDOWN_WITH_LINE_ITEMS]: () =>
        exportIndirectTaxFilingV2TaxBreakdownsWithLineItems({
          params: exportParams,
        }),
      [IndirectTaxFilingActionKey.GENERATE_REPORT]: () =>
        triggerIndirectTaxFilingsV2ContributionSync({
          params: { corporationId, filingIds },
        }),
      [IndirectTaxFilingActionKey.EXPORT_REPORT]: () =>
        exportIndirectTaxFilingV2LiabilityReport({
          params: {
            corporationId,
            filingIds,
            deliveryRecipients: userEmail ? [userEmail] : [],
            includeInvoiceDetail: true,
          },
        }),
      [IndirectTaxFilingActionKey.EXPORT_JURISDICTION_TAX_REPORT]: () =>
        exportIndirectTaxFilingV2TaxBreakdowns({
          params: {
            ...exportParams,
            exportMode: IndirectTaxFilingV2ExportMode.JURISDICTION_SPECIFIC,
          },
        }),
    };
  }, [corporationId, filingId, userEmail]);

  const handleAction = useCallback(
    async (key: IndirectTaxFilingActionKey) => {
      if (key === IndirectTaxFilingActionKey.VIEW_GENERATED_REPORTS) {
        jobsLogDialogVisibility.show();
        return;
      }
      if (
        IndirectTaxFilingActionsHelper.actionRequiresEmail(key) &&
        !userEmail
      ) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Could not determine your email for delivery',
        });
        return;
      }
      const runner = mutationRunners[key];
      if (!runner) {
        return;
      }
      setIsBusy(true);
      try {
        notifyResult(
          await runner(),
          IndirectTaxFilingActionsHelper.getActionSuccessMessage(key),
          IndirectTaxFilingActionsHelper.actionRefreshesFiling(key)
            ? onActionComplete
            : undefined
        );
      } finally {
        setIsBusy(false);
      }
    },
    [
      mutationRunners,
      userEmail,
      jobsLogDialogVisibility,
      notifyResult,
      onActionComplete,
      setNotification,
    ]
  );

  const actions = useMemo<IndirectTaxFilingMenuAction[]>(() => {
    const keys = [...regionActionKeys];
    if (isJurisdictionReportSupported) {
      keys.push(IndirectTaxFilingActionKey.EXPORT_JURISDICTION_TAX_REPORT);
    }
    return keys.map((key) => ({
      key,
      label: IndirectTaxFilingActionsHelper.getActionLabel(key),
      disabled:
        key === IndirectTaxFilingActionKey.VIEW_GENERATED_REPORTS
          ? false
          : isBusy ||
            (IndirectTaxFilingActionsHelper.actionRequiresEmail(key) &&
              !userEmail),
      onClick: () => handleAction(key),
    }));
  }, [
    regionActionKeys,
    isJurisdictionReportSupported,
    isBusy,
    userEmail,
    handleAction,
  ]);

  return {
    isInternal,
    isLoading: !loggedInUser && !userError,
    error: userError ?? null,
    actions,
    jobsLogDialogVisibility: {
      visible: jobsLogDialogVisibility.visible,
      show: jobsLogDialogVisibility.show,
      hide: jobsLogDialogVisibility.hide,
    },
    exportJobs,
  };
}
